import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Shield, CheckCircle2, Loader2, AlertTriangle, Gift, Sparkles } from "lucide-react";

export default function InviteLanding() {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [status, setStatus] = useState("loading"); // loading|valid|invalid|done
  const [form, setForm] = useState({
    plate: "", friend_name: "", friend_email: "", friend_phone: "", consent_privacy: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/r/${code}`);
        setInfo(data);
        setStatus("valid");
      } catch {
        setStatus("invalid");
      }
    })();
  }, [code]);

  const handle = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_privacy) {
      toast.error("Devi accettare l'informativa privacy");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/r/${code}/submit`, form);
      setStatus("done");
      toast.success("Richiesta inviata! Ti contatteremo a breve.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8F9] flex items-center justify-center p-6" data-testid="invite-landing-page">
      <div className="w-full max-w-xl fade-up">
        <div className="flex items-center justify-center mb-8">
          <img src="/brand/besafe-logo.png" alt="Besafe Broker" className="h-16 w-auto"/>
        </div>

        {status === "loading" && (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
            <Loader2 className="w-8 h-8 text-slate-400 mx-auto animate-spin"/>
          </div>
        )}

        {status === "invalid" && (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
            <AlertTriangle className="w-12 h-12 text-[#F39C12] mx-auto"/>
            <h1 className="mt-4 font-display text-2xl text-[#0F7B85]">Link non valido</h1>
            <p className="mt-2 text-slate-600">Il codice invito non esiste o è stato disattivato.</p>
            <Link to="/" className="inline-block mt-6 text-[#0F7B85] hover:text-[#F39C12] font-medium">Scopri Besafe Broker</Link>
          </div>
        )}

        {status === "valid" && info && (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 sm:p-10">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-600 bg-[#F7F8F9] px-3 py-1.5 rounded-full border border-slate-200">
              <Sparkles className="w-3.5 h-3.5 text-[#F39C12]" /> Invito personale · <span className="font-mono">{code}</span>
            </div>
            <h1 className="mt-5 font-display text-3xl sm:text-4xl text-[#0F7B85] tracking-tight leading-tight" data-testid="invite-title">
              <strong>{info.customer_name}</strong> ti consiglia Besafe Broker.
            </h1>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Lasciaci i tuoi dati: un nostro consulente ti contatterà con un preventivo dedicato sulla tua RCA.
              Se sottoscrivi, <strong>{info.customer_name.split(" ")[0]}</strong> riceverà 10€ di abbuono — senza
              alcun costo per te.
            </p>

            <div className="mt-6 grid grid-cols-3 gap-3 text-center">
              <div className="bg-[#F7F8F9] border border-slate-200 rounded-xl p-3">
                <Gift className="w-5 h-5 text-[#F39C12] mx-auto"/>
                <div className="text-xs text-slate-600 mt-1">Preventivo gratuito</div>
              </div>
              <div className="bg-[#F7F8F9] border border-slate-200 rounded-xl p-3">
                <CheckCircle2 className="w-5 h-5 text-[#0F7B85] mx-auto"/>
                <div className="text-xs text-slate-600 mt-1">Nessun obbligo</div>
              </div>
              <div className="bg-[#F7F8F9] border border-slate-200 rounded-xl p-3">
                <Shield className="w-5 h-5 text-[#0F7B85] mx-auto"/>
                <div className="text-xs text-slate-600 mt-1">GDPR-compliant</div>
              </div>
            </div>

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="invite-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Il tuo nome</label>
                  <input
                    type="text" required value={form.friend_name} onChange={handle("friend_name")}
                    data-testid="invite-name-input"
                    className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none"
                    placeholder="Luca Bianchi"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Targa</label>
                  <input
                    type="text" required value={form.plate} onChange={handle("plate")}
                    data-testid="invite-plate-input"
                    className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 uppercase font-mono focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none"
                    placeholder="AB123CD"
                    maxLength={10}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email</label>
                  <input
                    type="email" required value={form.friend_email} onChange={handle("friend_email")}
                    data-testid="invite-email-input"
                    className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none"
                    placeholder="luca@email.it"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Cellulare</label>
                  <input
                    type="tel" required value={form.friend_phone} onChange={handle("friend_phone")}
                    data-testid="invite-phone-input"
                    className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none"
                    placeholder="+39 333 1234567"
                  />
                </div>
              </div>
              <label className="flex items-start gap-3 bg-[#F7F8F9] border border-slate-200 rounded-xl p-4 cursor-pointer">
                <input
                  type="checkbox" checked={form.consent_privacy} onChange={handle("consent_privacy")}
                  data-testid="invite-consent-checkbox"
                  className="mt-1 w-4 h-4 rounded border-slate-300 text-[#0F7B85] focus:ring-[#0F7B85]"
                />
                <span className="text-sm text-slate-700 leading-relaxed">
                  Accetto il trattamento dei miei dati ai sensi del GDPR per ricevere un preventivo personalizzato
                  sulla RCA. Posso revocare il consenso in qualsiasi momento.
                </span>
              </label>
              <button
                type="submit" disabled={submitting} data-testid="invite-submit-button"
                className="w-full bg-[#F39C12] text-white hover:bg-[#E08E10] disabled:opacity-60 rounded-lg px-6 py-3.5 font-medium transition-colors inline-flex items-center justify-center gap-2"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>}
                Richiedi preventivo
              </button>
            </form>
          </div>
        )}

        {status === "done" && info && (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-green-50 mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-700"/>
            </div>
            <h1 className="mt-4 font-display text-2xl text-[#0F7B85]">Ci siamo!</h1>
            <p className="mt-2 text-slate-600">
              Un nostro consulente ti contatterà nei prossimi giorni per il preventivo.
            </p>
            <Link to="/" className="inline-block mt-6 text-[#0F7B85] hover:text-[#F39C12] font-medium">Scopri Besafe Broker</Link>
          </div>
        )}
      </div>
    </div>
  );
}
